import React, { useRef, useEffect, useState, useCallback } from 'react';
import { FiEye, FiEyeOff, FiLock, FiUnlock, FiTrash2, FiChevronRight } from 'react-icons/fi';
import useEditorStore from '../../stores/useEditorStore';
import { applySelectionStyle } from '../../utils/selectionStyleUtils';

// 공통 아이콘 버튼 스타일
const iconBtnStyle = {
  padding: '4px', border: 'none', background: 'none',
  cursor: 'pointer', display: 'flex', alignItems: 'center',
};

// 레이어 타입 아이콘
const TYPE_ICONS = {
  rect: '\u25A2', circle: '\u25CB', triangle: '\u25B3', line: '\u2015',
  'i-text': 'T', text: 'T', textbox: 'T',
  image: '\uD83D\uDDBC', path: '\u270F', group: '\uD83D\uDCC1',
};

const LayerPanel = () => {
  const {
    canvas, layers, selectedObjects, pushHistory,
    syncLayersFromCanvas, editingGroupId,
  } = useEditorStore();

  const scrollContainerRef = useRef(null);
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const nameInputRef = useRef(null);

  // DnD 상태
  const [dragState, setDragState] = useState(null); // { id, sourceParentId }
  const [dropTarget, setDropTarget] = useState(null); // { id, position: 'before'|'after'|'inside' }

  // --- 유틸리티 함수 ---

  const findObjectById = (objects, targetId) => {
    for (const obj of objects) {
      if (obj.id === targetId) return obj;
      if (obj.type === 'group') {
        const found = findObjectById(obj.getObjects(), targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const findParentGroupIds = (layerList, targetId, path = []) => {
    for (const layer of layerList) {
      if (layer.id === targetId) return path;
      if (layer.type === 'group' && layer.children) {
        const found = findParentGroupIds(layer.children, targetId, [...path, layer.id]);
        if (found) return found;
      }
    }
    return null;
  };

  // 부모 그룹 찾기 (canvas objects 기준)
  const findParentGroup = useCallback((objects, targetId) => {
    for (const obj of objects) {
      if (obj.type === 'group') {
        const children = obj.getObjects();
        if (children.some(c => c.id === targetId)) return obj;
        const found = findParentGroup(children, targetId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const isSelected = (layerId) => selectedObjects.some(obj => obj.id === layerId);

  // --- 이름 편집 ---

  const startEditingName = (layerId, currentName, e) => {
    e.stopPropagation();
    setEditingNameId(layerId);
    setEditingNameValue(currentName);
  };

  const commitNameEdit = () => {
    if (!editingNameId || !canvas) return;
    const obj = findObjectById(canvas.getObjects(), editingNameId);
    if (obj && editingNameValue.trim()) {
      obj.name = editingNameValue.trim();
      syncLayersFromCanvas();
    }
    setEditingNameId(null);
    setEditingNameValue('');
  };

  const cancelNameEdit = () => {
    setEditingNameId(null);
    setEditingNameValue('');
  };

  useEffect(() => {
    if (editingNameId && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [editingNameId]);

  // --- 선택 이벤트 시 자동 스크롤 ---

  useEffect(() => {
    if (!selectedObjects.length || !scrollContainerRef.current) return;
    const selectedId = selectedObjects[0]?.id;
    if (!selectedId) return;

    if (canvas) {
      const parentIds = findParentGroupIds(layers, selectedId);
      if (parentIds?.length > 0) {
        let needSync = false;
        for (const pid of parentIds) {
          const groupObj = findObjectById(canvas.getObjects(), pid);
          if (groupObj && !groupObj._layerExpanded) {
            groupObj._layerExpanded = true;
            needSync = true;
          }
        }
        if (needSync) syncLayersFromCanvas();
      }
    }

    requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      const el = container?.querySelector(`[data-layer-id="${selectedId}"]`);
      if (!el || !container) return;

      const containerRect = container.getBoundingClientRect();
      const elTop = el.getBoundingClientRect().top - containerRect.top;

      if (elTop < containerRect.height * 0.1 || elTop > containerRect.height * 0.9) {
        container.scrollBy({ top: elTop - containerRect.height * 0.2, behavior: 'smooth' });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObjects]);

  // --- 클릭 핸들러 ---

  const handleLayerClick = (layerId) => {
    if (!canvas) return;
    if (editingGroupId) useEditorStore.getState().exitGroupEditing();

    const obj = canvas.getObjects().find(o => o.id === layerId);
    if (obj && obj.selectable !== false) {
      canvas.setActiveObject(obj);
      canvas.renderAll();
    }
  };

  const selectChildOnCanvas = (targetId, rootGroupId) => {
    if (!canvas) return;
    const objects = canvas.getObjects();
    const rootGroup = objects.find(o => o.id === rootGroupId);
    if (!rootGroup || rootGroup.type !== 'group') return;

    const findPath = (group, id) => {
      for (const child of group.getObjects()) {
        if (child.id === id) return [child];
        if (child.type === 'group') {
          const sub = findPath(child, id);
          if (sub) return [child, ...sub];
        }
      }
      return null;
    };

    const path = findPath(rootGroup, targetId);
    if (!path?.length) return;

    const resetAllGroups = (objs) => {
      for (const obj of objs) {
        if (obj.type === 'group') {
          obj.set({ interactive: false, subTargetCheck: false });
          resetAllGroups(obj.getObjects());
        }
      }
    };
    resetAllGroups(objects);

    const activateGroup = (group) => {
      group.set({ subTargetCheck: true, interactive: true });
      group.setCoords();
      group.getObjects().forEach(c => {
        c.setCoords?.();
        applySelectionStyle(c);
      });
    };

    activateGroup(rootGroup);
    for (const node of path) {
      if (node.type === 'group' && node.id !== targetId) {
        activateGroup(node);
      }
    }

    let directParentId = rootGroup.id;
    for (let i = path.length - 2; i >= 0; i--) {
      if (path[i].type === 'group') { directParentId = path[i].id; break; }
    }
    useEditorStore.getState().setEditingGroupId(directParentId);

    canvas.setActiveObject(path[path.length - 1]);
    canvas.requestRenderAll();
  };

  // --- 레이어 액션 핸들러 ---

  const handleVisibilityToggle = (layerId, e) => {
    e.stopPropagation();
    if (!canvas) return;
    const obj = findObjectById(canvas.getObjects(), layerId);
    if (obj) { obj.visible = !obj.visible; canvas.renderAll(); syncLayersFromCanvas(); }
  };

  const handleLockToggle = (layerId, e) => {
    e.stopPropagation();
    if (!canvas) return;
    const obj = findObjectById(canvas.getObjects(), layerId);
    if (obj) {
      obj.selectable = !obj.selectable;
      obj.evented = obj.selectable;
      canvas.renderAll();
      syncLayersFromCanvas();
    }
  };

  const handleDelete = (layerId, e) => {
    e.stopPropagation();
    if (!canvas) return;
    const obj = canvas.getObjects().find(o => o.id === layerId);
    if (obj) { canvas.remove(obj); canvas.renderAll(); syncLayersFromCanvas(); pushHistory(); }
  };

  const handleUngroup = (groupId, e) => {
    e.stopPropagation();
    if (!canvas) return;
    const group = canvas.getObjects().find(o => o.id === groupId);
    if (!group || group.type !== 'group') return;
    canvas.setActiveObject(group);
    useEditorStore.getState().ungroupObjects();
  };

  const handleToggleExpand = (groupId, e) => {
    e.stopPropagation();
    if (!canvas) return;
    const group = findObjectById(canvas.getObjects(), groupId);
    if (group) { group._layerExpanded = !group._layerExpanded; syncLayersFromCanvas(); }
  };

  // --- Drag & Drop ---
  //
  // UI 패널은 layers를 reverse()하여 표시 (높은 z-index가 위).
  // Fabric 배열은 낮은 인덱스 = 낮은 z-index (뒤에 렌더).
  // 따라서 UI의 "위(before)" = Fabric 인덱스 증가, "아래(after)" = 인덱스 감소.
  //
  // 인디케이터 통일: 'before'는 이전 레이어의 'after'로 정규화하여
  // 같은 갭(gap)에 항상 동일한 borderBottom 선을 표시.
  // 예외: 맨 위 레이어의 'before'만 borderTop 사용.
  //
  // executeDrop은 DOM onDrop이 아닌 dropTarget 상태를 기준으로 동작.
  // 레이어 영역 밖 드롭은 onDragEnd에서 처리.

  const clearDragState = () => {
    setDragState(null);
    setDropTarget(null);
  };

  // 화면 표시 순서대로 레이어 ID 목록 (flat)
  const getFlatLayerIds = useCallback(() => {
    const result = [];
    const collect = (list) => {
      for (const l of list) {
        result.push(l.id);
        if (l.children) collect(l.children);
      }
    };
    collect([...layers].reverse());
    return result;
  }, [layers]);

  const handleDragStart = (e, layerId, parentGroupId = null) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', layerId);
    setDragState({ id: layerId, sourceParentId: parentGroupId });
  };

  // 마우스 Y 위치로 before/inside/after 판정
  const getDropPosition = (e, element) => {
    const rect = element.getBoundingClientRect();
    const ratio = (e.clientY - rect.top) / rect.height;
    if (ratio < 0.25) return 'before';
    if (ratio > 0.75) return 'after';
    return 'inside';
  };

  // before를 이전 레이어의 after로 정규화 (인디케이터 위치 통일)
  const normalizeDropPosition = (layerId, pos) => {
    if (pos !== 'before') return { id: layerId, position: pos };

    const flatIds = getFlatLayerIds();
    const idx = flatIds.indexOf(layerId);
    if (idx > 0) {
      const prevId = flatIds[idx - 1];
      if (prevId !== dragState.id) {
        return { id: prevId, position: 'after' };
      }
    }
    // 맨 위 레이어이거나 이전이 드래그 대상 → before 유지
    return { id: layerId, position: 'before' };
  };

  const handleDragOverRow = (e, layerId, layerType) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';

    if (!dragState || dragState.id === layerId) {
      setDropTarget(null);
      return;
    }

    let pos = getDropPosition(e, e.currentTarget);
    if (pos === 'inside' && layerType !== 'group') pos = 'after';

    setDropTarget(normalizeDropPosition(layerId, pos));
  };

  // 그룹 bounds 재계산: 모든 자식을 포함하도록 width/height/위치 갱신
  const recalcGroupBounds = (group) => {
    const children = group.getObjects();
    if (children.length === 0) return;
    const sx = group.scaleX || 1;
    const sy = group.scaleY || 1;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    children.forEach(child => {
      const w = (child.width || 0) * (child.scaleX || 1);
      const h = (child.height || 0) * (child.scaleY || 1);
      let l = child.left, t = child.top;
      if (child.originX === 'center') l -= w / 2;
      else if (child.originX === 'right') l -= w;
      if (child.originY === 'center') t -= h / 2;
      else if (child.originY === 'bottom') t -= h;
      minX = Math.min(minX, l);
      minY = Math.min(minY, t);
      maxX = Math.max(maxX, l + w);
      maxY = Math.max(maxY, t + h);
    });

    const newCX = (minX + maxX) / 2;
    const newCY = (minY + maxY) / 2;

    // 자식 좌표를 새 중심 기준으로 이동
    children.forEach(child => {
      child.set({ left: child.left - newCX, top: child.top - newCY });
      child.setCoords();
    });

    // 현재 중심점을 left/top + origin으로 계산 (부모 로컬 좌표 유지)
    const oldW = (group.width || 0) * sx;
    const oldH = (group.height || 0) * sy;
    let centerX = group.left, centerY = group.top;
    if (group.originX === 'left') centerX += oldW / 2;
    else if (group.originX === 'right') centerX -= oldW / 2;
    if (group.originY === 'top') centerY += oldH / 2;
    else if (group.originY === 'bottom') centerY -= oldH / 2;

    // 중심 이동
    centerX += newCX * sx;
    centerY += newCY * sy;

    // 새 크기로 left/top 역산
    const newW = maxX - minX;
    const newH = maxY - minY;
    let newLeft = centerX, newTop = centerY;
    if (group.originX === 'left') newLeft -= newW * sx / 2;
    else if (group.originX === 'right') newLeft += newW * sx / 2;
    if (group.originY === 'top') newTop -= newH * sy / 2;
    else if (group.originY === 'bottom') newTop += newH * sy / 2;

    group.set({ width: newW, height: newH, left: newLeft, top: newTop });
    group.setCoords();

    // 부모 그룹이 있으면 재귀적으로 bounds 갱신
    const parentGroup = findParentGroup(canvas.getObjects(), group.id);
    if (parentGroup) recalcGroupBounds(parentGroup);
  };

  // 드래그 대상을 원래 위치에서 분리
  const detachDraggedObject = (draggedObj) => {
    const objects = canvas.getObjects();
    const sourceParent = findParentGroup(objects, draggedObj.id);
    if (sourceParent) {
      // group.remove()가 자동으로 로컬→절대 좌표 변환 수행
      sourceParent.remove(draggedObj);
      if (sourceParent.getObjects().length === 0) {
        canvas.remove(sourceParent);
      } else {
        recalcGroupBounds(sourceParent);
      }
    } else {
      canvas.remove(draggedObj);
    }
  };

  // 대상 위치에 삽입 (UI before = Fabric 인덱스 +1, UI after = Fabric 인덱스)
  const insertAtTarget = (draggedObj, targetId, position) => {
    const targetObj = findObjectById(canvas.getObjects(), targetId);
    if (!targetObj) {
      canvas.add(draggedObj);
      return;
    }

    if (position === 'inside' && targetObj.type === 'group') {
      targetObj.add(draggedObj);
      recalcGroupBounds(targetObj);
      targetObj._layerExpanded = true;
      return;
    }

    const targetParent = findParentGroup(canvas.getObjects(), targetId);
    const insertIdx = position === 'before' ? 1 : 0; // before → +1, after → +0

    if (targetParent) {
      const siblings = targetParent.getObjects();
      const targetIdx = siblings.findIndex(c => c.id === targetId);
      const allChildren = [...siblings];
      targetParent.removeAll();
      allChildren.splice(targetIdx + insertIdx, 0, draggedObj);
      allChildren.forEach(c => targetParent.add(c));
      recalcGroupBounds(targetParent);
    } else {
      const targetIdx = canvas.getObjects().findIndex(o => o.id === targetId);
      canvas.add(draggedObj);
      canvas.moveObjectTo(draggedObj, targetIdx + insertIdx);
    }
  };

  const executeDrop = (e) => {
    if (e) e.preventDefault();
    if (!dragState || !canvas || !dropTarget || dragState.id === dropTarget.id) {
      clearDragState();
      return;
    }

    const draggedObj = findObjectById(canvas.getObjects(), dragState.id);
    if (!draggedObj) { clearDragState(); return; }

    detachDraggedObject(draggedObj);
    insertAtTarget(draggedObj, dropTarget.id, dropTarget.position);

    clearDragState();
    canvas.renderAll();
    syncLayersFromCanvas();
    pushHistory();
  };

  // dragend는 드롭 위치와 무관하게 항상 발생 → 영역 밖 드롭 처리
  const handleDragEnd = (e) => {
    if (dropTarget && dragState) {
      executeDrop(e);
    } else {
      clearDragState();
    }
  };

  // --- 드롭 인디케이터 스타일 ---

  const getDropIndicatorStyle = (layerId) => {
    if (!dropTarget || dropTarget.id !== layerId) return {};
    if (dropTarget.position === 'before') return { boxShadow: 'inset 0 2px 0 0 #3b82f6' };
    if (dropTarget.position === 'after') return { boxShadow: 'inset 0 -2px 0 0 #3b82f6' };
    if (dropTarget.position === 'inside') return { outline: '2px solid #8b5cf6', outlineOffset: '-2px' };
    return {};
  };

  // --- 렌더링 ---

  const reversedLayers = [...layers].reverse();

  // 이름 렌더링 (편집 가능)
  const renderName = (layer, isChild = false) => {
    if (editingNameId === layer.id) {
      return (
        <input
          ref={nameInputRef}
          type="text"
          value={editingNameValue}
          onChange={(e) => setEditingNameValue(e.target.value)}
          onBlur={commitNameEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitNameEdit();
            if (e.key === 'Escape') cancelNameEdit();
          }}
          onClick={(e) => e.stopPropagation()}
          style={{
            flex: 1, fontSize: 13, color: '#374151', border: '1px solid #3b82f6',
            borderRadius: '3px', padding: '1px 4px', outline: 'none', background: '#ffffff',
            minWidth: 0,
          }}
        />
      );
    }

    return (
      <span
        onDoubleClick={(e) => startEditingName(layer.id, layer.name, e)}
        style={{
          flex: 1, fontSize: 13, color: isChild ? '#6b7280' : '#374151',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          cursor: 'text',
        }}
        title="더블클릭하여 이름 변경"
      >
        {layer.name}
      </span>
    );
  };

  // 레이어 행 렌더링
  const renderLayerRow = (layer, options = {}) => {
    const { indent = 0, isChild = false, parentGroupId = null, rootGroupId = null } = options;
    const selected = isSelected(layer.id);
    const isDragging = dragState?.id === layer.id;

    const handleClick = isChild
      ? () => selectChildOnCanvas(layer.id, rootGroupId || parentGroupId)
      : () => handleLayerClick(layer.id);

    return (
      <div
        key={layer.id}
        data-layer-id={layer.id}
        draggable
        onDragStart={(e) => handleDragStart(e, layer.id, parentGroupId)}
        onDragOver={(e) => handleDragOverRow(e, layer.id, layer.type)}
        onDrop={(e) => executeDrop(e)}
        onDragEnd={handleDragEnd}
        onClick={handleClick}
        style={{
          display: 'flex', alignItems: 'center',
          padding: '8px 12px', paddingLeft: `${12 + indent * 20}px`,
          marginBottom: '2px', borderRadius: '6px',
          backgroundColor: selected ? (isChild ? '#f5f3ff' : '#eff6ff') : 'transparent',
          borderWidth: '1px', borderStyle: 'solid',
          borderColor: selected ? (isChild ? '#8b5cf6' : '#3b82f6') : 'transparent',
          cursor: layer.locked ? 'not-allowed' : 'grab',
          opacity: isDragging ? 0.4 : (layer.visible ? 1 : 0.5),
          transition: 'background-color 0.15s, border-color 0.15s',
          ...getDropIndicatorStyle(layer.id),
        }}
      >
        <span style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, marginRight: 8, flexShrink: 0 }}>
          {TYPE_ICONS[layer.type] || '\u25A0'}
        </span>
        {renderName(layer, isChild)}
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
          {!isChild && (
            <>
              <button onClick={(e) => handleVisibilityToggle(layer.id, e)} style={iconBtnStyle} title={layer.visible ? '숨기기' : '표시'}>
                {layer.visible ? <FiEye size={14} color="#6b7280" /> : <FiEyeOff size={14} color="#d1d5db" />}
              </button>
              <button onClick={(e) => handleLockToggle(layer.id, e)} style={iconBtnStyle} title={layer.locked ? '잠금 해제' : '잠금'}>
                {layer.locked ? <FiLock size={14} color="#ef4444" /> : <FiUnlock size={14} color="#6b7280" />}
              </button>
              <button onClick={(e) => handleDelete(layer.id, e)} style={iconBtnStyle} title="삭제">
                <FiTrash2 size={14} color="#ef4444" />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  // 그룹 행 렌더링 (재귀)
  const renderGroupLayer = (layer, depth = 0, rootGroupId = null) => {
    const isExpanded = layer.expanded !== false;
    const children = layer.children || [];
    const effectiveRootId = rootGroupId || layer.id;
    const isSubGroup = rootGroupId !== null;
    const selected = isSelected(layer.id);
    const isDragging = dragState?.id === layer.id;

    const handleGroupClick = isSubGroup
      ? () => selectChildOnCanvas(layer.id, effectiveRootId)
      : () => handleLayerClick(layer.id);

    return (
      <React.Fragment key={layer.id}>
        <div
          data-layer-id={layer.id}
          draggable
          onDragStart={(e) => handleDragStart(e, layer.id, isSubGroup ? rootGroupId : null)}
          onDragOver={(e) => handleDragOverRow(e, layer.id, 'group')}
          onDrop={(e) => executeDrop(e)}
          onDragEnd={handleDragEnd}
          onClick={handleGroupClick}
          style={{
            display: 'flex', alignItems: 'center', padding: '8px 12px',
            marginBottom: '2px', borderRadius: '6px',
            backgroundColor: selected ? '#f5f3ff' : 'transparent',
            borderWidth: '1px', borderStyle: 'solid',
            borderColor: selected ? '#8b5cf6' : 'transparent',
            cursor: layer.locked ? 'not-allowed' : 'grab',
            opacity: isDragging ? 0.4 : (layer.visible ? 1 : 0.5),
            transition: 'background-color 0.15s, border-color 0.15s',
            ...getDropIndicatorStyle(layer.id),
          }}
        >
          <button
            onClick={(e) => handleToggleExpand(layer.id, e)}
            style={{ ...iconBtnStyle, padding: '2px', marginRight: '4px', transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
          >
            <FiChevronRight size={12} color="#6b7280" />
          </button>
          <span style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, marginRight: 8, flexShrink: 0 }}>
            {TYPE_ICONS.group}
          </span>
          {editingNameId === layer.id ? (
            <input
              ref={nameInputRef}
              type="text"
              value={editingNameValue}
              onChange={(e) => setEditingNameValue(e.target.value)}
              onBlur={commitNameEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitNameEdit();
                if (e.key === 'Escape') cancelNameEdit();
              }}
              onClick={(e) => e.stopPropagation()}
              style={{
                flex: 1, fontSize: 13, fontWeight: 600, color: '#374151',
                border: '1px solid #3b82f6', borderRadius: '3px', padding: '1px 4px',
                outline: 'none', background: '#ffffff', minWidth: 0,
              }}
            />
          ) : (
            <span
              onDoubleClick={(e) => startEditingName(layer.id, layer.name, e)}
              style={{ flex: 1, fontSize: 13, color: '#374151', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text' }}
              title="더블클릭하여 이름 변경"
            >
              {layer.name}
            </span>
          )}
          <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
            <button
              onClick={(e) => handleUngroup(layer.id, e)}
              style={{ padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '10px', display: 'flex', alignItems: 'center' }}
              title="그룹 해제"
            >
              해제
            </button>
            <button onClick={(e) => handleVisibilityToggle(layer.id, e)} style={iconBtnStyle} title={layer.visible ? '숨기기' : '표시'}>
              {layer.visible ? <FiEye size={14} color="#6b7280" /> : <FiEyeOff size={14} color="#d1d5db" />}
            </button>
            <button onClick={(e) => handleLockToggle(layer.id, e)} style={iconBtnStyle} title={layer.locked ? '잠금 해제' : '잠금'}>
              {layer.locked ? <FiLock size={14} color="#ef4444" /> : <FiUnlock size={14} color="#6b7280" />}
            </button>
            <button onClick={(e) => handleDelete(layer.id, e)} style={iconBtnStyle} title="삭제"><FiTrash2 size={14} color="#ef4444" /></button>
          </div>
        </div>

        {isExpanded && children.length > 0 && (
          <div style={{ marginLeft: '16px' }}>
            {[...children].reverse().map((child, idx, arr) => {
              const isLast = idx === arr.length - 1;
              return (
                <div key={child.id} style={{ display: 'flex', minHeight: 0 }}>
                  {/* 트리 꺾은선 */}
                  <div style={{ width: '20px', flexShrink: 0, position: 'relative' }}>
                    {/* 세로선: 마지막이 아니면 전체 높이, 마지막이면 절반 */}
                    <div style={{
                      position: 'absolute', left: '8px', top: 0, width: '2px',
                      height: isLast ? '50%' : '100%', backgroundColor: '#d1d5db',
                    }} />
                    {/* 가로선 */}
                    <div style={{
                      position: 'absolute', left: '8px', top: '50%', width: '10px',
                      height: '2px', backgroundColor: '#d1d5db',
                    }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {child.type === 'group'
                      ? renderGroupLayer(child, depth + 1, effectiveRootId)
                      : renderLayerRow(child, { indent: 0, isChild: true, parentGroupId: layer.id, rootGroupId: effectiveRootId })
                    }
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <div style={{ width: '100%', backgroundColor: '#ffffff', borderTop: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, fontSize: 14, color: '#374151' }}>
        레이어
      </div>
      <div
        ref={scrollContainerRef}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => executeDrop(e)}
        style={{ flex: 1, overflowY: 'auto', padding: '8px' }}
      >
        {reversedLayers.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
            레이어가 없습니다
          </div>
        ) : (
          reversedLayers.map((layer) =>
            layer.type === 'group' ? renderGroupLayer(layer) : renderLayerRow(layer)
          )
        )}
      </div>
    </div>
  );
};

export default LayerPanel;
